import React, { useState, useRef, useEffect } from 'react';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { getAccessToken } from '../../../lib/auth/tokenService';
import { fetchWithAuth } from '../../../lib/http/httpClient';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useAuth } from "app/provider";
import { Combobox } from "components/ui/combobox";

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
}
const STORAGE_KEY = "selectedCustId";

const AiPage: React.FC = () => {
    const { setBreadcrumbs, setHeaderBorder } = useBreadcrumbs();
    const { userType, customers } = useAuth();

    const [selectedCustId, setSelectedCustId] = useState<string>();
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setBreadcrumbs([
            { label: 'Ask MRC AI', href: '/ai' }
        ]);
        setHeaderBorder(false);
        return () => setHeaderBorder(true);
    }, [setBreadcrumbs, setHeaderBorder]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!customers?.length) {
            setSelectedCustId(undefined);
            return;
        }

        if (userType === "Internal") {
            const storedCustId = localStorage.getItem(STORAGE_KEY);

            const isStoredValid = storedCustId ? customers.some(c => c.cust_code === storedCustId) : false;

            const defaultCustId: string | undefined =
                isStoredValid
                    ? storedCustId ?? undefined
                    : customers[0]?.cust_code ?? undefined;

            setSelectedCustId(defaultCustId);

            if (defaultCustId) {
                localStorage.setItem(STORAGE_KEY, defaultCustId);
            }
            return;
        }
        setSelectedCustId(customers[0]?.cust_code ?? undefined);
    }, [userType, customers]);

    const customerOptions =
        customers?.map(c => ({
            value: c.cust_code,
            label: c.cust_name,
        })) || [];

    // Handle Streaming Implementation
    const handleSendStreaming = async (text: string) => {
        if (!selectedCustId) {
            setError("Customer not selected");
            return;
        }
        const userMsgId = Date.now().toString();
        const userMsg: Message = { id: userMsgId, role: 'user', content: text };
        setMessages(prev => [...prev, userMsg]);
        setIsLoading(true);
        setError(null);

        const assistantMsgId = (Date.now() + 1).toString();
        // Initial empty assistant message
        setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: '' }]);

        try {
            // Use the new fetchWithAuth from httpClient to handle token refresh automatically
            const res = await fetchWithAuth('/api/v1/cp/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Authorization header is handled by fetchWithAuth
                },
                body: { // fetchWithAuth expects object or FormData, not stringified JSON for body if not FormData
                    message: text,
                    conversation_id: conversationId,
                    cust_id: selectedCustId
                }
            });

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(`Server error: ${res.status} ${errText}`);
            }

            if (!res.body) throw new Error("No response body");

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedContent = '';
            let loop = true;
            let buffer = '';

            while (loop) {
                const { done, value } = await reader.read();
                if (done) {
                    loop = false;
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                const lines = buffer.split('\n');
                // The last line might be incomplete, so we save it back to buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed || !trimmed.startsWith('data: ')) continue;

                    const jsonStr = trimmed.replace('data: ', '').trim();
                    if (jsonStr === '[DONE]') { // Standard OpenAI format often uses [DONE], but our spec says {"type":"done"}
                        loop = false;
                        break;
                    }

                    try {
                        const event = JSON.parse(jsonStr);

                        // Handle Event Types
                        if (event.type === 'conversation_id') {
                            setConversationId(event.conversation_id);
                        } else if (event.type === 'text') {
                            accumulatedContent += event.content;
                            // Update the specific message in state
                            setMessages(currentMessages => {
                                return currentMessages.map(msg =>
                                    msg.id === assistantMsgId ? { ...msg, content: accumulatedContent } : msg
                                );
                            });
                        } else if (event.type === 'tool_call') {
                            // Tool calls are no longer visualized
                            console.log('Tool call:', event.tool_name);
                        } else if (event.type === 'done') {
                            loop = false;
                        } else if (event.type === 'error') {
                            throw new Error(event.message);
                        }
                    } catch (e) {
                        console.error("Error parsing SSE event", e, jsonStr);
                    }
                }
            }

        } catch (err: any) {
            console.error(err);
            setError(err.message);
            // Append error to chat content
            setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, content: m.content + "\n\n⚠️ Error: " + (err.message || 'Unknown error') } : m));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCustomerChange = (custId: string) => {
        const nextCustId = custId || undefined;
        setSelectedCustId(nextCustId);
        if (nextCustId) {
            localStorage.setItem(STORAGE_KEY, nextCustId);
        }

        // Reset chat when customer changes
        setMessages([]);
        setConversationId(null);
        setError(null);
    };


    return (
        <div className="flex flex-col h-full bg-white relative">
            {userType === "Internal" && (
                <div className="px-4">
                        <div className="w-[260px]">
                            <Combobox
                                options={customerOptions}
                                value={selectedCustId || ""}
                                onValueChange={handleCustomerChange}
                                placeholder="All Customers"
                                searchPlaceholder="Search customers..."
                                emptyText="No customer found."
                                buttonClassName="h-8 text-xs font-normal text-gray-600"
                            />
                        </div>
                </div>
            )}
            {/* Header / Empty State */}
            {messages.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        How can I help you today?
                    </h1>
                    <p className="text-gray-500 max-w-md mb-8">
                        I can help you track shipments, find information, and manage your logistics operations.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
                        {['How many shipments are currently headed to Pune?', 'Show me all my CNs currently on hold', 'Show me all CNs expected to arrive today', 'Summarize my shipment statuses'].map((suggestion) => (
                            <button
                                key={suggestion}
                                onClick={() => handleSendStreaming(suggestion)}
                                className="p-4 text-sm text-left bg-gray-50 hover:bg-white hover:shadow-md border border-gray-100 rounded-xl transition-all duration-200 text-gray-700"
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto px-4 py-6 scroll-smooth" id="chat-container">
                    <div className="max-w-3xl mx-auto flex flex-col">
                        {messages.map((msg) => (
                            <ChatMessage
                                key={msg.id}
                                role={msg.role}
                                content={msg.content}
                                isStreaming={msg.role === 'assistant' && isLoading && msg.id === messages[messages.length - 1].id}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                        {error && (
                            <div className="mx-auto mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100 flex items-center gap-2">
                                <span>Something went wrong. Please try again.</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Input Area */}
            <ChatInput onSend={handleSendStreaming} isLoading={isLoading} />
        </div>
    );
};

export default AiPage;
